import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Check, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import type { ProcessType, RecruitmentProcess, CreateStepTemplateInput } from '@pms/shared';
import { recruitmentService } from '@/services/recruitment.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs } from '@/components/ui/tabs';
import { sectionLabel, getErrorMessage } from '@/components/recruitment/utils';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

export function RecruitmentConfigSection() {
  const [type, setType] = useState<ProcessType>('joining');
  const queryClient = useQueryClient();

  const q = useQuery({
    queryKey: ['recruitment', 'process', type],
    queryFn: () => recruitmentService.getProcess(type),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['recruitment', 'process', type] });

  const [newSection, setNewSection] = useState('');
  const [editingSection, setEditingSection] = useState<{ id: string; name: string } | null>(null);
  const [pendingDeleteSection, setPendingDeleteSection] = useState<{ id: string; name: string } | null>(null);
  const [pendingDeleteStep, setPendingDeleteStep] = useState<{ id: string; name: string } | null>(null);
  const [newStep, setNewStep] = useState<{ section: string; step_name: string; is_required: boolean; sla_hours: string }>(
    { section: '', step_name: '', is_required: true, sla_hours: '' },
  );

  const settings = useMutation({
    mutationFn: (patch: Partial<RecruitmentProcess>) => recruitmentService.updateProcessSettings(q.data!.process.id, patch),
    onSuccess: () => { toast.success('Settings saved'); void invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // ── Main step (section) mutations ──
  const addSection = useMutation({
    mutationFn: () => recruitmentService.createSection(q.data!.process.id, newSection.trim()),
    onSuccess: () => { toast.success('Main step added'); setNewSection(''); void invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const renameSection = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => recruitmentService.updateSection(id, { name }),
    onSuccess: () => { toast.success('Main step renamed'); void invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const deleteSection = useMutation({
    mutationFn: (id: string) => recruitmentService.deleteSection(id),
    onSuccess: () => { toast.success('Main step removed'); void invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const reorderSections = useMutation({
    mutationFn: (ids: string[]) => recruitmentService.reorderSections(q.data!.process.id, ids),
    onSuccess: () => void invalidate(),
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const moveSection = (index: number, dir: -1 | 1) => {
    const secs = q.data!.sections;
    const target = index + dir;
    if (target < 0 || target >= secs.length) return;
    const ids = secs.map((s) => s.id);
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    reorderSections.mutate(ids);
  };
  const saveRename = () => {
    if (!editingSection) return;
    const name = editingSection.name.trim();
    const original = (q.data?.sections ?? []).find((s) => s.id === editingSection.id);
    if (name && original && name !== original.name) renameSection.mutate({ id: editingSection.id, name });
    setEditingSection(null);
  };

  // ── Sub-step mutations ──
  const addStep = useMutation({
    mutationFn: (section: string) => {
      const payload: CreateStepTemplateInput = {
        section,
        step_name: newStep.step_name.trim(),
        is_required: newStep.is_required,
        sla_hours: newStep.sla_hours ? Number(newStep.sla_hours) : null,
        step_order: q.data!.steps.length,
      };
      return recruitmentService.createStep(q.data!.process.id, payload);
    },
    onSuccess: () => { toast.success('Step added'); setNewStep((s) => ({ ...s, step_name: '', sla_hours: '' })); void invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const updateStep = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) => recruitmentService.updateStep(id, patch),
    onSuccess: () => void invalidate(),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deleteStep = useMutation({
    mutationFn: (id: string) => recruitmentService.deleteStep(id),
    onSuccess: () => { toast.success('Step removed'); void invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => recruitmentService.reorderSteps(q.data!.process.id, ids),
    onSuccess: () => void invalidate(),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const move = (index: number, dir: -1 | 1) => {
    const steps = q.data!.steps;
    const target = index + dir;
    if (target < 0 || target >= steps.length) return;
    const ids = steps.map((s) => s.id);
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    reorder.mutate(ids);
  };

  const process = q.data?.process;
  const sections = q.data?.sections ?? [];
  // The dropdown value must be a section that still exists; fall back to the first.
  const selectedSection = sections.some((s) => s.name === newStep.section)
    ? newStep.section
    : (sections[0]?.name ?? '');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Configure the onboarding &amp; exit checklist — main steps, sub-steps, and settings.</p>
        <Tabs
          tabs={[{ key: 'joining', label: 'Joining Process' }, { key: 'exit', label: 'Exit Process' }]}
          active={type}
          onChange={(k) => { setType(k as ProcessType); setNewStep((s) => ({ ...s, section: '' })); setNewSection(''); }}
        />
      </div>

      {q.isLoading || !q.data ? (
        <div className="flex items-center justify-center py-16 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_minmax(300px,360px)]">
          <div className="space-y-4">
            {/* Main Steps (sections) manager */}
            <div className="rounded-lg border border-border/60 bg-card">
              <div className="border-b border-border/60 px-4 py-3 text-sm font-semibold">Main Steps ({sections.length})</div>
              {sections.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No main steps yet. Add one below.</p>
              ) : (
                <div className="divide-y divide-border/40">
                  {sections.map((sec, i) => (
                    <div key={sec.id} className="flex items-center gap-2 px-4 py-2.5">
                      <div className="flex flex-col">
                        <button type="button" onClick={() => moveSection(i, -1)} disabled={i === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                      </div>
                      {editingSection?.id === sec.id ? (
                        <>
                          <Input
                            autoFocus
                            value={editingSection.name}
                            onChange={(e) => setEditingSection({ id: sec.id, name: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setEditingSection(null); }}
                            className="h-8 flex-1"
                          />
                          <button
                            type="button"
                            title="Save"
                            disabled={!editingSection.name.trim()}
                            onClick={saveRename}
                            className="rounded p-1.5 text-primary hover:bg-primary/10 disabled:opacity-40"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            title="Cancel"
                            onClick={() => setEditingSection(null)}
                            className="rounded p-1.5 text-muted-foreground hover:bg-muted"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-sm font-medium text-foreground">{sectionLabel(sec.name)}</span>
                          <button
                            type="button"
                            title="Rename"
                            onClick={() => setEditingSection({ id: sec.id, name: sec.name })}
                            className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            onClick={() => setPendingDeleteSection({ id: sec.id, name: sectionLabel(sec.name) })}
                            className="rounded p-1.5 text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 border-t border-border/60 p-3">
                <Input
                  value={newSection}
                  onChange={(e) => setNewSection(e.target.value)}
                  placeholder="New main step name…"
                  onKeyDown={(e) => { if (e.key === 'Enter' && newSection.trim()) addSection.mutate(); }}
                />
                <Button size="sm" variant="outline" disabled={!newSection.trim() || addSection.isPending} onClick={() => addSection.mutate()}>
                  <Plus className="h-4 w-4" /> Add Main Step
                </Button>
              </div>
            </div>

            {/* Sub-step builder */}
            <div className="rounded-lg border border-border/60 bg-card">
              <div className="border-b border-border/60 px-4 py-3 text-sm font-semibold">Sub-steps ({q.data.steps.length})</div>
              {q.data.steps.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">No sub-steps yet. Add one below.</p>
              ) : (
                <div className="divide-y divide-border/40">
                  {q.data.steps.map((s, i) => (
                    <div key={s.id} className="flex items-center gap-2 px-4 py-2.5">
                      <div className="flex flex-col">
                        <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => move(i, 1)} disabled={i === q.data.steps.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                      </div>
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{sectionLabel(s.section)}</span>
                      <span className="flex-1 text-sm font-medium text-foreground">{s.step_name}</span>
                      <label className="flex items-center gap-1 text-xs text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={s.is_required}
                          onChange={(e) => updateStep.mutate({ id: s.id, patch: { is_required: e.target.checked } })}
                        />
                        Required
                      </label>
                      <button type="button" onClick={() => setPendingDeleteStep({ id: s.id, name: s.step_name })} className="rounded p-1.5 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add sub-step */}
            <div className="rounded-lg border border-border/60 bg-card p-4">
              <p className="mb-3 text-sm font-semibold">Add Sub-step</p>
              {sections.length === 0 ? (
                <p className="text-sm text-muted-foreground">Add a Main Step first, then you can add sub-steps under it.</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Main Step (Section)</Label>
                      <Select value={selectedSection || undefined} onValueChange={(v) => setNewStep((s) => ({ ...s, section: v }))}>
                        <SelectTrigger><SelectValue placeholder="Select a main step" /></SelectTrigger>
                        <SelectContent>
                          {sections.map((sec) => <SelectItem key={sec.id} value={sec.name}>{sectionLabel(sec.name)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>SLA (hours, optional)</Label>
                      <Input type="number" value={newStep.sla_hours} onChange={(e) => setNewStep((s) => ({ ...s, sla_hours: e.target.value }))} />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label>Step Name</Label>
                      <Input value={newStep.step_name} onChange={(e) => setNewStep((s) => ({ ...s, step_name: e.target.value }))} placeholder="e.g. Document Collection" />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-muted-foreground">
                      <input type="checkbox" checked={newStep.is_required} onChange={(e) => setNewStep((s) => ({ ...s, is_required: e.target.checked }))} />
                      Required step
                    </label>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button size="sm" disabled={!newStep.step_name.trim() || !selectedSection || addStep.isPending} onClick={() => addStep.mutate(selectedSection)}>
                      <Plus className="h-4 w-4" /> Add Step
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Settings */}
          <div className="rounded-lg border border-border/60 bg-card p-4">
            <p className="mb-3 text-sm font-semibold">Process Settings</p>
            {process && (
              <div className="space-y-3">
                <Toggle label="Active" checked={process.is_active} onChange={(v) => settings.mutate({ is_active: v })} />
                <Toggle label="Auto-start on candidate conversion" checked={process.auto_start_enabled} onChange={(v) => settings.mutate({ auto_start_enabled: v })} />
                <Toggle label="Allow manual trigger" checked={process.manual_trigger_allowed} onChange={(v) => settings.mutate({ manual_trigger_allowed: v })} />
                <Toggle label="Allow re-initiate" checked={process.allow_reinitiate} onChange={(v) => settings.mutate({ allow_reinitiate: v })} />
                {type === 'exit' && (
                  <Toggle label="Lock employee during exit" checked={process.lock_employee_during_exit} onChange={(v) => settings.mutate({ lock_employee_during_exit: v })} />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDeleteSection}
        onOpenChange={(o) => { if (!o) setPendingDeleteSection(null); }}
        title={`Delete "${pendingDeleteSection?.name}" and all its sub-steps?`}
        description="This main step and all sub-steps under it will be permanently removed."
        confirmLabel="Delete"
        onConfirm={() => { if (pendingDeleteSection) deleteSection.mutate(pendingDeleteSection.id); }}
      />

      <ConfirmDialog
        open={!!pendingDeleteStep}
        onOpenChange={(o) => { if (!o) setPendingDeleteStep(null); }}
        title={`Delete "${pendingDeleteStep?.name}"?`}
        description="This sub-step will be permanently removed."
        confirmLabel="Delete"
        onConfirm={() => { if (pendingDeleteStep) deleteStep.mutate(pendingDeleteStep.id); }}
      />
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 text-sm">
      <span className="text-foreground">{label}</span>
      <Switch checked={checked} onChange={onChange} aria-label={label} />
    </label>
  );
}
